import { type ADFMarkType } from '@canvas/adf-types';

import { plainTextToAdf } from './conversions';

type AdfMark = {
  type?: string;
  attrs?: Record<string, unknown>;
};

type AdfNode = {
  type?: string;
  text?: string;
  marks?: AdfMark[];
  content?: AdfNode[];
  [key: string]: unknown;
};

const isAdfDocument = (value: unknown): value is AdfNode =>
  Boolean(
    value &&
      typeof value === 'object' &&
      (value as AdfNode).type === 'doc' &&
      Array.isArray((value as AdfNode).content)
  );

const parseAdfDocument = (value: string): AdfNode | undefined => {
  try {
    const parsed = JSON.parse(value);
    return isAdfDocument(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const addMarkToNode = (
  node: AdfNode,
  markType: ADFMarkType
): { node: AdfNode; changed: boolean } => {
  let changed = false;
  let nextNode = node;

  if (node.type === 'text') {
    const marks = Array.isArray(node.marks) ? node.marks : [];
    if (!marks.some((mark) => mark.type === markType)) {
      nextNode = {
        ...node,
        marks: [...marks, { type: markType }],
      };
      changed = true;
    }
  }

  if (Array.isArray(node.content)) {
    const nextContent: AdfNode[] = [];
    let contentChanged = false;

    for (let index = 0; index < node.content.length; index++) {
      const result = addMarkToNode(node.content[index], markType);
      nextContent.push(result.node);
      contentChanged = contentChanged || result.changed;
    }

    if (contentChanged) {
      nextNode = {
        ...nextNode,
        content: nextContent,
      };
      changed = true;
    }
  }

  return { node: nextNode, changed };
};

export const addMarkToAdfText = (value: string, markType: ADFMarkType): string => {
  if (value === '') {
    return value;
  }

  const parsedDocument = parseAdfDocument(value);
  const adfDocument = parsedDocument ?? (plainTextToAdf(value) as unknown as AdfNode);
  const result = addMarkToNode(adfDocument, markType);

  if (!result.changed) {
    return value;
  }

  return JSON.stringify(result.node);
};
